import React, { useState, useEffect } from "react";
import { Box, Button, Typography, Link, Grid, TextField } from "@mui/material";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import sso from "../../assets/sso.jpg";
import { jwtDecode } from "jwt-decode";
import { BACKEND_BASE_URL } from "../../constants";
import { useAuth } from "../../AuthContext";
import { use } from "react";
import { msalInstance } from "../../msalConfig";

const ProtectedRoute = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const { email, setEmail, isAuthenticated ,setIsAuthenticated } = useAuth();
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();

  useEffect(() => {
    const userToken = localStorage.getItem("userToken");
    if (userToken) {
      try {
        const decodedToken = jwtDecode(userToken);
        const currentTime = Math.floor(Date.now() / 1000);

        // Check if token is expired
        if (decodedToken.exp > currentTime) {
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error("Invalid token in localStorage:", error);
      }
    }

    // Check if there are accounts (SSO login is active)
    if (accounts.length !== 0) {
      // set the first account as active for silent token acquisition
      if (!msalInstance.getActiveAccount()) {
        msalInstance.setActiveAccount(accounts[0]);
      }
      setIsAuthenticated(true);
    }
  }, [accounts]);

  const handleSSOSignIn = async () => {
    const loginRequest = {
      scopes: ["User.Read", "openid", "profile", "email"],
    };

    try {
      setLoading(true);
      const response = await instance.loginPopup(loginRequest);

      if (response.account) {
        msalInstance.setActiveAccount(response.account);
        const data = response.account.name.split(", ");
        const userData = {
          email: response.account.username,
          firstName: data[1] || "",
          lastName: data[0] || "",
        };

        setIsAuthenticated(true); // Set as authenticated after SSO login
        setEmail(userData.email);
        navigate("/");
      }
    } catch (error) {
      alert("Authentication failed!");
      console.error("Authentication error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCredentialSignIn = async () => {
    setLoading(true);

    const payload = {
      username,
      password,
    };

    try {
      const response = await fetch(`${BACKEND_BASE_URL}/get_guest_login/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();

        if (data.message === "Login successful") {
          localStorage.setItem("userToken", data.token);

          setIsAuthenticated(true);
          console.log('email is', email);
          navigate("/");
        } else {
          alert("Login failed: " + data.message);
        }
      } else {
        alert("Failed to connect to the server!");
      }
    } catch (error) {
      console.error("Error during login:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!isAuthenticated ? (
        <Grid container sx={{ height: "100vh" }}>
          {/* Left Side - Sign In Section */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "white",
              position: "relative",
            }}
          >
            {/* KEARNEY at Top Left */}
            <Typography
              variant="h4"
              component="h1"
              sx={{
                position: "absolute",
                top: 20,
                left: 20,
                fontWeight: "500",
              }}
            >
              KEARNEY
            </Typography>

            <Box sx={{ textAlign: "center", p: 4 }}>
              <Typography
                variant="h5"
                component="h2"
                sx={{ fontWeight: "bold", mb: 2 }}
              >
                Sign In
              </Typography>
              <Typography variant="body1" sx={{ mb: 8 }}>
                Welcome back! Please sign in to your account.
              </Typography>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {/* Sign In using credentials */}
                <TextField
                  label="Username"
                  variant="outlined"
                  fullWidth
                  sx={{ mb: 2, width: "70%" }}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
                <TextField
                  label="Password"
                  type="password"
                  variant="outlined"
                  fullWidth
                  sx={{ mb: 3, width: "70%" }}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />

                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ mb: 2, width: "70%" }}
                  onClick={handleCredentialSignIn}
                  disabled={loading}
                >
                  Sign In using credentials
                </Button>
              </div>

              <Typography variant="subtitle1" color="textSecondary" mb={2}>
                Or
              </Typography>

              {/* Sign In using SSO */}
              <Button
                variant="contained"
                color="secondary"
                sx={{ mb: 5, width: "70%" }}
                onClick={handleSSOSignIn}
                disabled={loading}
              >
                Sign In using Kearney SSO
              </Button>

              <Typography variant="body2" color="textSecondary">
                By clicking on 'Sign In', you confirm to have read the 'Kearney
                Privacy Statement' and provide consent to use your personal
                information like Name and Email ID
              </Typography>
              <Link
                href="#"
                underline="always"
                sx={{ display: "block", mt: 1 }}
              >
                View Privacy Statement
              </Link>
            </Box>
          </Grid>

          {/* Right Side - Image Section */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundImage: `url(${sso})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              "::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
              },
            }}
          >
            <Box
              sx={{
                position: "relative",
                color: "white",
                fontSize: "2rem",
                fontWeight: "bold",
                zIndex: 1,
              }}
            >
              Magic Cube
            </Box>
          </Grid>
        </Grid>
      ) : (
        <>{children}</>
      )}
    </>
  );
};

export default ProtectedRoute;
